/** Slide break used in Text and Text Slides exercise bodies. */
import { normalizeAudience, normalizeLabel, ONLINE_LESSON_AUDIENCES } from './onlineLessonAudience.js';

export const TEXT_SLIDE_BREAK = '======';

export function parseTextSlidePages(body) {
    let chunks = String(body || '').split(/(?:\r?\n)?======(?:\r?\n)?/);
    chunks = chunks.filter((p) => !/^=+$/.test(p) && p.trim() !== '');
    if (chunks.length === 0) chunks = [''];
    return chunks;
}

export function appendTextSlide(body) {
    const t = String(body || '').replace(/\s+$/, '');
    return t ? `${t}\n======\n` : '';
}

/** Whether Text Slides "show to different students" mode is on. */
export function isTextSlidesDifferentStudents(exercise) {
    return Boolean(exercise?.assessment?.showToDifferentStudents);
}

/**
 * Audiences parallel to parsed pages (`all` | `A` | `B` | `C` …).
 * Pads/trims to match page count; missing entries default to `all`.
 */
export function getTextSlideAudiences(exercise, pageCount) {
    const n = Math.max(0, Number(pageCount) || 0);
    const raw = Array.isArray(exercise?.assessment?.slideAudiences)
        ? exercise.assessment.slideAudiences
        : [];
    const out = [];
    for (let i = 0; i < n; i++) {
        out.push(normalizeAudience(raw[i]));
    }
    return out;
}

export function syncTextSlideAudiences(existing, pageCount) {
    const prev = Array.isArray(existing) ? existing : [];
    const n = Math.max(0, Number(pageCount) || 0);
    const out = [];
    for (let i = 0; i < n; i++) {
        out.push(normalizeAudience(prev[i]));
    }
    return out;
}

/**
 * Logical nav steps for "Show to different students":
 * each All page is its own step; a contiguous run of letter pages is ONE parallel group.
 * Teacher path feels like: All → ABC… → All (not All → A → B → C → All).
 *
 * @returns {{ type: 'all'|'group', indices: number[] }[]}
 */
export function getTextSlideLogicalSteps(audiences = []) {
    const steps = [];
    let i = 0;
    const list = Array.isArray(audiences) ? audiences : [];
    while (i < list.length) {
        if (normalizeAudience(list[i]) === 'all') {
            steps.push({ type: 'all', indices: [i] });
            i += 1;
            continue;
        }
        const indices = [];
        while (i < list.length && normalizeAudience(list[i]) !== 'all') {
            indices.push(i);
            i += 1;
        }
        if (indices.length) steps.push({ type: 'group', indices });
    }
    return steps;
}

export function findTextSlideLogicalStep(audiences, pageIndex) {
    const steps = getTextSlideLogicalSteps(audiences);
    const idx = Math.max(0, Number(pageIndex) || 0);
    const stepIndex = steps.findIndex((step) => step.indices.includes(idx));
    if (stepIndex < 0) return { steps, stepIndex: -1, step: null };
    return { steps, stepIndex, step: steps[stepIndex] };
}

/**
 * Page the teacher lands on when moving ±1 logical step from `fromPage`.
 * Returns null if there is no next/prev step.
 */
export function resolveTeacherLogicalPage(audiences, fromPage, delta) {
    const { steps, stepIndex } = findTextSlideLogicalStep(audiences, fromPage);
    if (stepIndex < 0 || !steps.length) return null;
    const nextIndex = stepIndex + (Number(delta) || 0);
    if (nextIndex < 0 || nextIndex >= steps.length) return null;
    const step = steps[nextIndex];
    // Prefer staying on the same letter when entering/leaving isn't needed —
    // entering a group opens on its first page (teacher can pick A–F via dots).
    return step.indices[0];
}

/**
 * While the teacher is on any page inside a letter group, each student sees
 * their own letter's page in that same group (parallel), not "Waiting…".
 * Shared All pages stay synced 1:1.
 *
 * @returns {number|null} page index, or null if the student has no matching slide
 */
export function resolveStudentTextSlidePage(audiences, teacherPage, studentLabel) {
    const list = Array.isArray(audiences) ? audiences : [];
    if (!list.length) return 0;
    const teacherIdx = Math.min(Math.max(0, Number(teacherPage) || 0), list.length - 1);
    const { step } = findTextSlideLogicalStep(list, teacherIdx);
    if (!step) return teacherIdx;
    if (step.type === 'all') return teacherIdx;

    const label = normalizeLabel(studentLabel);
    if (!label) return null;

    const matches = step.indices.filter((i) => normalizeAudience(list[i]) === label);
    if (!matches.length) return null;

    // If the teacher is previewing the k-th slide of letter X, show the student's
    // k-th slide of their letter when multiple pages share one letter.
    const teacherAud = normalizeAudience(list[teacherIdx]);
    const teacherLetterPages = step.indices.filter((i) => normalizeAudience(list[i]) === teacherAud);
    const pos = Math.max(0, teacherLetterPages.indexOf(teacherIdx));
    return matches[Math.min(pos, matches.length - 1)];
}

export { ONLINE_LESSON_AUDIENCES };
